import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  alpha,
  useTheme as useMuiTheme,
  Tooltip,
  Fade,
} from '@mui/material';
import { 
  ExpandMore, 
  ChevronRight, 
  Edit, 
  Delete, 
  FolderOpen, 
  Folder,
  AccountBalance,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Category } from '../../types/categories.types';
import { categoriesService } from '../../services/categories.service';

interface CategoryTreeProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}

interface CategoryNodeProps {
  category: Category;
  level: number;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}

const CategoryNode: React.FC<CategoryNodeProps> = ({ 
  category, 
  level, 
  onEdit, 
  onDelete 
}) => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();
  const isRTL = language === 'ar';
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const hasChildren = category.children && category.children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('categoryId', category.categoryId.toString());
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const draggedCategoryId = parseInt(e.dataTransfer.getData('categoryId'));
    const targetCategoryId = category.categoryId;

    if (draggedCategoryId === targetCategoryId) return;
    if (draggedCategoryId === category.parentCategoryId) return;

    try {
      // Update parent via API
      await categoriesService.updateParent(draggedCategoryId, targetCategoryId);
      toast.success(isRTL ? 'تم نقل التصنيف بنجاح' : 'Category moved successfully', {
        position: 'top-center',
        autoClose: 2000
      });
      window.location.reload();
    } catch (err) {
      toast.error(isRTL ? 'فشل نقل التصنيف' : 'Failed to move category', {
        position: 'top-center',
        autoClose: 3000
      });
    }
  };

  const translations = {
    en: { 
      active: 'Active', 
      inactive: 'Inactive',
      edit: 'Edit',
      delete: 'Delete',
      hasAccount: 'Linked to Expense Account',
    },
    ar: { 
      active: 'نشط', 
      inactive: 'غير نشط',
      edit: 'تعديل',
      delete: 'حذف',
      hasAccount: 'مربوط بحساب مصروفات',
    },
  };
  const t = translations[language];

  const hasExpenseAccount = !!(category as any).expenseAccountId;

  // ألوان مختلفة حسب المستوى
  const getLevelColor = () => {
    const colors = [
      mode === 'dark' ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)',
      mode === 'dark' ? 'rgba(118, 75, 162, 0.15)' : 'rgba(118, 75, 162, 0.08)',
      mode === 'dark' ? 'rgba(230, 126, 34, 0.15)' : 'rgba(230, 126, 34, 0.08)',
      mode === 'dark' ? 'rgba(46, 204, 113, 0.15)' : 'rgba(46, 204, 113, 0.08)',
    ];
    return colors[level % colors.length];
  };

  const getBorderColor = () => {
    const colors = [
      '#667eea',
      '#764ba2',
      '#e67e22',
      '#2ecc71',
    ];
    return colors[level % colors.length];
  };

  return (
    <Fade in timeout={300}>
      <Box sx={{ width: '100%' }}>
        <Box
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            mb: 1,
            marginLeft: isRTL ? 0 : `${level * 32}px`,
            marginRight: isRTL ? `${level * 32}px` : 0,
            background: isDragOver 
              ? (mode === 'dark' ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.25)')
              : hovered 
                ? (mode === 'dark' 
                    ? alpha(muiTheme.palette.primary.main, 0.2)
                    : alpha(muiTheme.palette.primary.main, 0.1))
                : getLevelColor(),
            borderLeft: isRTL ? 'none' : `4px solid ${getBorderColor()}`,
            borderRight: isRTL ? `4px solid ${getBorderColor()}` : 'none',
            boxShadow: isDragOver
              ? '0 0 20px rgba(102, 126, 234, 0.6)'
              : hovered 
                ? (mode === 'dark'
                    ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                    : '0 4px 12px rgba(102, 126, 234, 0.15)')
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isDragging ? 'scale(0.95) rotate(2deg)' : hovered ? 'translateY(-2px)' : 'translateY(0)',
            opacity: isDragging ? 0.4 : 1,
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          {/* Expand/Collapse Button */}
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ 
              mr: isRTL ? 0 : 1, 
              ml: isRTL ? 1 : 0,
              visibility: hasChildren ? 'visible' : 'hidden',
              transition: 'transform 0.3s',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              color: getBorderColor(),
            }}
          >
            <ExpandMore />
          </IconButton>

          {/* Folder Icon */}
          <Box sx={{ 
            mr: isRTL ? 0 : 1.5, 
            ml: isRTL ? 1.5 : 0,
            display: 'flex',
            alignItems: 'center',
          }}>
            {hasChildren ? (
              expanded ? (
                <FolderOpen 
                  sx={{ 
                    color: getBorderColor(),
                    fontSize: 28,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                  }} 
                />
              ) : (
                <Folder 
                  sx={{ 
                    color: getBorderColor(),
                    fontSize: 28,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                  }} 
                />
              )
            ) : (
              <Box 
                sx={{ 
                  width: 28, 
                  height: 28,
                  borderRadius: '50%',
                  background: alpha(getBorderColor(), 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: getBorderColor(),
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Code */}
            <Chip
              label={category.categoryCode}
              size="small"
              sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                bgcolor: alpha(getBorderColor(), 0.2),
                color: mode === 'dark' ? 'white' : getBorderColor(),
                borderRadius: 1.5,
                px: 1.5,
                fontSize: '0.9rem',
                minWidth: 100,
              }}
            />

            {/* Name */}
            <Typography 
              variant="body1" 
              sx={{ 
                flex: 1,
                fontWeight: 500,
                fontSize: '1rem',
              }}
            >
              {isRTL ? category.categoryNameAr : category.categoryName}
            </Typography>

            {/* Expense Account Indicator */}
            {hasExpenseAccount && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                      {t.hasAccount}
                    </Typography>
                    {(category as any).expenseAccountCode && (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {(category as any).expenseAccountCode} - {isRTL ? (category as any).expenseAccountNameAr : (category as any).expenseAccountName}
                      </Typography>
                    )}
                  </Box>
                }
              >
                <AccountBalance 
                  sx={{ 
                    color: '#2ecc71',
                    fontSize: 20,
                  }} 
                />
              </Tooltip>
            )}

            {/* Status */}
            <Chip
              label={category.isActive ? t.active : t.inactive}
              color={category.isActive ? 'success' : 'default'}
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: 1.5,
                minWidth: 70,
              }}
            />
          </Box>

          {/* Actions */}
          <Fade in={hovered} timeout={200}>
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5,
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.2s',
            }}>
              <Tooltip title={t.edit}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(category);
                  }}
                  sx={{
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(muiTheme.palette.primary.main, 0.2),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Edit fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.delete}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(category);
                  }}
                  sx={{
                    bgcolor: alpha(muiTheme.palette.error.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(muiTheme.palette.error.main, 0.2),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        </Box>

        {/* Children */}
        {hasChildren && (
          <Collapse 
            in={expanded} 
            timeout={300}
            sx={{
              '& .MuiCollapse-wrapperInner': {
                pt: 0.5,
              }
            }}
          >
            <Box>
              {category.children!.map((child) => (
                <CategoryNode
                  key={child.categoryId}
                  category={child}
                  level={level + 1}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    </Fade>
  );
};

const CategoryTree: React.FC<CategoryTreeProps> = ({ 
  categories, 
  onEdit, 
  onDelete 
}) => {
  return (
    <Box sx={{ 
      width: '100%',
      '& > *:last-child': {
        mb: 0,
      }
    }}>
      {categories.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          color: 'text.secondary',
        }}>
          <FolderOpen sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" sx={{ opacity: 0.6 }}>
            No categories found
          </Typography>
        </Box>
      ) : (
        categories.map((category, index) => (
          <CategoryNode
            key={category.categoryId}
            category={category}
            level={0}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      )}
    </Box>
  );
};

export default CategoryTree;